import Button from "@atoms/button";
import { Typography } from "@atoms/typography";
import { BookingFormFields } from "@molecules/booking-form-fields";
import { BookingPriceResult } from "@molecules/booking-price-result";
import { DialogActions } from "@molecules/dialog-actions";
import { DialogContent } from "@molecules/dialog-content";
import { DialogTitle } from "@molecules/dialog-title";
import { Dialog } from "@organisms/dialog";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Trans } from "next-i18next";
import styled from "styled-components";
import { Loader } from "@atoms/loader/loader";
import Link from "next/link";
import { SearchForm } from "@organisms/searcher";
import * as dateFns from "date-fns";
import {
  BookingFormData,
  GetCalendarHandler,
} from "@molecules/booking-form-fields/interfaces";
import { PaymentMethods } from "@molecules/payment-methods";
import { Input } from "@atoms/input";

export type CalculatePriceRequest = { checkIn: Date; checkOut: Date };
export type CalculatePriceResponse =
  | { ok: true; price: number; installmentPaymentEnabled: boolean }
  | { ok: false; error: string };
export type CalculatePriceHandler = (
  data: CalculatePriceRequest
) => Promise<CalculatePriceResponse>;

export type BookingHandler = (data: BookingFormData) => Promise<boolean>;

export type DialogBookingProps = {
  open: boolean;
  searchFormData?: SearchForm;
  onClose?: () => void;
  onBooking?: BookingHandler;
  onCalculatePrice?: CalculatePriceHandler;
  onGetCalendar: GetCalendarHandler;
};

export const DialogBooking: FC<DialogBookingProps> = ({
  open,
  searchFormData,
  onClose,
  onBooking,
  onCalculatePrice,
  onGetCalendar,
}) => {
  const [sendingForm, setSendingForm] = useState(false);
  const [installmentPaymentEnabled, setInstallmentPaymentEnabled] =
    useState(false);
  const [price, setPrice] = useState<number | null>(null);
  const [notAvailable, setNotAvailable] = useState<boolean | null>(null);
  const [calculating, setCalculating] = useState(false);
  const [errorSendingForm, setErrorSendingForm] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<BookingFormData>({
    mode: "onChange",
    defaultValues: {
      name: "",
      email: "",
      mobile: "",
      guests: searchFormData?.guests,
      period: {
        checkIn:
          typeof searchFormData?.checkIn === "string"
            ? new Date(searchFormData?.checkIn)
            : searchFormData?.checkIn,
        checkOut:
          typeof searchFormData?.checkOut === "string"
            ? new Date(searchFormData?.checkOut)
            : searchFormData?.checkOut,
      },
      paymentMethod: "full",
      acceptConditions: false,
    },
  });

  const [period, paymentMethod] = watch(["period", "paymentMethod"]);

  const nights = useMemo(
    () =>
      period && period.checkIn && period.checkOut
        ? dateFns.differenceInDays(period.checkOut, period.checkIn)
        : undefined,
    [period]
  );

  const handleBooking = useCallback(
    async (data: BookingFormData) => {
      setSendingForm(true);
      setErrorSendingForm(false);
      try {
        const result = await Promise.resolve(onBooking && onBooking(data));
        // NOTE: if a valid result, keep state sending form, we're redirecting to payment platform
        if (!result) {
          setErrorSendingForm(true);
          setSendingForm(false);
        }
      } catch (e) {
        setErrorSendingForm(true);
        setSendingForm(false);
      }
    },
    [onBooking]
  );

  const handleSelectPaymentMethod = useCallback(
    (method: string) => {
      setValue("paymentMethod", method);
    },
    [setValue]
  );

  useEffect(() => {
    if (
      !!!period ||
      !!!period.checkIn ||
      !!!period.checkOut ||
      period.checkOut === period.checkIn ||
      !!!onCalculatePrice
    ) {
      setPrice(null);
      setNotAvailable(null);
      return;
    }

    (async function () {
      setCalculating(true);
      setNotAvailable(false);
      try {
        const priceResult = await onCalculatePrice({
          checkIn: period.checkIn as Date,
          checkOut: period.checkOut as Date,
        });
        if (priceResult.ok) {
          setPrice(priceResult.price);
          setInstallmentPaymentEnabled(priceResult.installmentPaymentEnabled);
          setNotAvailable(false);
          if (!priceResult.installmentPaymentEnabled)
            setValue("paymentMethod", "full");
        } else {
          setPrice(null);
          setNotAvailable(true);
          setInstallmentPaymentEnabled(false);
          setValue("paymentMethod", "full");
        }
      } catch (e) {
        setPrice(null);
        setNotAvailable(true);
        setInstallmentPaymentEnabled(false);
        setValue("paymentMethod", "full");
      } finally {
        setCalculating(false);
      }
    })();
  }, [period, onCalculatePrice, setValue]);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        <Trans>Booking form</Trans>
      </DialogTitle>
      <StyledForm onSubmit={handleSubmit(handleBooking)}>
        <DialogContent>
          <Typography paragraph>
            Fill in the following form to be redirected to the payment gateway.
          </Typography>
          <BookingFormFields
            disabled={sendingForm}
            control={control}
            errors={errors}
            register={register}
            onGetCalendar={onGetCalendar}
          />
          <PaymentMethods
            installmentPaymentEnabled={installmentPaymentEnabled}
            onSelectPaymentMethod={handleSelectPaymentMethod}
            paymentMethod={paymentMethod}
          />
          <div style={{ marginTop: "0.5rem" }}>
            <Typography
              variant="body2"
              as="label"
              color={errors.acceptConditions ? "error" : "default"}
            >
              <input
                type="checkbox"
                {...register("acceptConditions", {
                  required: true,
                  value: true,
                })}
              />
              Aceptar las condiciones de uso y politicas de cancelación
            </Typography>
          </div>
        </DialogContent>
        <DialogActions>
          {errorSendingForm && (
            <Typography color="error">
              It has not been possible to generate the payment request, please,
              contact us through the <Link href="/contact">contact form</Link>,
              or retry submitting the form.
            </Typography>
          )}
          {notAvailable ? (
            <Typography color="error">
              There is no availability for the indicated date and num. of
              nights.
            </Typography>
          ) : calculating ? (
            <div className="loader-container">
              <Loader variant="dots" />
            </div>
          ) : (
            <BookingPriceResult price={price} nights={nights} />
          )}
          <Button
            variant="primary"
            type="submit"
            showLoading={sendingForm}
            disabled={sendingForm}
          >
            <Trans>Book now</Trans>
          </Button>
        </DialogActions>
      </StyledForm>
    </Dialog>
  );
};

const StyledForm = styled.form`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;
